import {seatingsPricingOptionModalOpened, seatingsPricingOptionModalSelected} from '@wix/bi-logger-events-uou/v2'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {getPricingOptionLabel, getPricingOptions} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation, useVisitorBi} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useCallback, useEffect, useState} from 'react'
import {InputDialog, RadioButton, RadioButtonGroup, RadioButtonTheme, Text} from 'wix-ui-tpa/cssVars'
import {useCurrencyFormatter} from '../../../../../../../../commons/hooks/currency'
import {useColors} from '../../../../../hooks/colors'
import {useAppActions, useAppSelector} from '../../../../../hooks/runtime'
import {getEvent} from '../../../../../selectors/event'
import {getLastKnownOrigin, getShowPricingOptionsPlace} from '../../../../../selectors/seating/mode'
import {
  getAreaPricingOptionsTotalPrice,
  isPlaceArea,
  pricingOptionPlaceId,
} from '../../../../../selectors/seating/place'
import {getFees} from '../../../../../selectors/tax-and-fee'
import {TicketAreaCounter} from '../../ticket/ticket-area-counter'
import {TicketInfoTexts} from '../../ticket/ticket-info/ticket-info-texts'
import {Footer} from '../footer'
import {classes, st} from './pricing-option-modal.st.css'

export const PricingOptionModal = () => {
  const {t} = useTranslation()
  const {isMobile} = useEnvironment()
  const bi = useVisitorBi()
  const {darkColor} = useColors()
  const {getFormattedMoney, getTicketPriceText} = useCurrencyFormatter()
  const {setShowPricingOptionsPlace, selectPlacePricingOption} = useAppActions()
  const [localValue, setLocalValue] = useState(1)

  const place = useAppSelector(getShowPricingOptionsPlace)
  const event = useAppSelector(getEvent)
  const origin = useAppSelector(getLastKnownOrigin)

  const pricingOptions = getPricingOptions(place.ticket)
  const area = isPlaceArea(place)

  const [selectedOptionId, setSelectedOptionId] = useState<string>(pricingOptions[0].id)

  const placeId = area ? pricingOptionPlaceId(place.planPlaceId, selectedOptionId) : place.id
  const selectedPricingOptionIds = Array(localValue).fill(selectedOptionId)
  const biFields = {
    pricing_options: JSON.stringify(pricingOptions),
    ticket_definition_id: place.ticket.id,
    is_whole_table: false,
    seat_guid: place.planPlaceId,
    element_type: place.elementType,
    element_guid: place.planPlaceId,
    event_id: event.id,
  }

  useEffect(() => {
    focusElement({selector: `${hookToAttributeSelector(DH.PRICING_OPTION)} input`, canFocus: true})

    bi.report(seatingsPricingOptionModalOpened(biFields))
  }, [])

  const close = useCallback(() => {
    setShowPricingOptionsPlace(null)

    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Close',
        ...biFields,
      }),
    )
  }, [setShowPricingOptionsPlace])

  const confirm = useCallback(() => {
    selectPlacePricingOption({placeId, pricingOptionIds: selectedPricingOptionIds, origin})
    setShowPricingOptionsPlace(null)

    bi.report(
      seatingsPricingOptionModalSelected({
        selection: 'Add to basket',
        selected_pricing_options: JSON.stringify([pricingOptions.find(({id}) => id === selectedOptionId)]),
        ...biFields,
      }),
    )
  }, [place.id, selectedOptionId, selectPlacePricingOption, setShowPricingOptionsPlace, selectedPricingOptionIds])

  return (
    <InputDialog
      title={t('seating_priceOptionsSingle_title')}
      isOpen={true}
      onClose={close}
      className={isMobile ? classes.mobileModalRoot : undefined}
      fullscreen={isMobile}
      data-hook={DH.PRICING_OPTIONS_MODAL}
      closeButtonAriaLabel={t('a11y.close')}
      ariaLabel={t('seating_priceOptionsSingle_title')}
      customFooter={
        <Footer
          primaryButtonLabel={t('seating_priceOptionsSingle_confirmButton')}
          primaryButtonClick={confirm}
          secondaryButtonLabel={t('seating_priceOptionsSingle_cancelButton')}
          secondaryButtonClick={close}
        >
          {area ? (
            <Text tagName="div" className={classes.areaTotalPrice}>
              {t('seating_areaReservation', {
                price: getAreaPricingOptionsTotalPrice({
                  event,
                  place: {...place, selectedPricingOptionIds},
                  getTicketPriceText,
                }),
              })}
            </Text>
          ) : null}
        </Footer>
      }
    >
      <div className={st(classes.optionsContainer, {mobile: isMobile})}>
        <div className={!area ? classes.ticketInfoTexts : undefined}>
          {isMobile ? <TicketInfoTexts place={place} inPricingOptionsModal /> : null}
        </div>
        {area ? (
          <div className={st(classes.counterContainer, {mobile: isMobile})}>
            <Text className={st(classes.ticketInfo, {mobile: isMobile}, classes.ticketQuantity)}>
              {t('seatings_pricingOptions_quantity')}
            </Text>
            <TicketAreaCounter
              value={localValue}
              setValue={setLocalValue}
              origin={origin}
              selectedPricingOptionId={placeId}
              place={place}
            />
          </div>
        ) : null}
        {area ? (
          <Text className={st(classes.ticketInfo, {mobile: isMobile}, classes.ticketName)}>{place.ticket.name}</Text>
        ) : null}
        <RadioButtonGroup
          className={classes.options}
          data-hook={DH.PRICING_OPTIONS}
          name="pricing-option"
          onChange={value => setSelectedOptionId(value)}
          theme={RadioButtonTheme.Box}
          value={selectedOptionId}
        >
          {pricingOptions.map(({id, price}) => (
            <RadioButton
              data-hook={DH.PRICING_OPTION}
              key={id}
              className={classNames(st(classes.option, {blackAndWhite: !darkColor || id !== selectedOptionId}), {
                [classes.selectedOption]: id === selectedOptionId,
              })}
              value={id}
              label={getPricingOptionLabel(place.ticket, id)}
              subtext={getFees({
                event,
                ticket: place.ticket,
                t,
                getFormattedMoney,
                overridePrice: price.value,
              })}
            />
          ))}
        </RadioButtonGroup>
      </div>
    </InputDialog>
  )
}
